import React, { memo, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import Select, { components } from "react-select";
import styles from "./styles.module.scss";
import Button from "app/components/Button/ButtonVariant";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import closeIcon from "../../../../public/images/close-icon.svg";
import DollarIcon from "../../../../public/images/dollar-ico.svg";
import AccountCircleIcon from "../../../../public/images/account-circle-icon.svg";
import { StudentProfileModel } from "app/models/scheduling/StudentProfileModel";
import { StudentProgramRegistrationModel } from "app/models/scheduling/StudentProgramRegistrationModel";
import { schedulingFetchGet, schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import SchedulingUserSelector from "app/components/SchedulingUserSelector";
import UserItems from "../UserItems";
import PaymentMethodDropdown from "app/components/PaymentMethodDropdown";
import {
  OnlinePaymentTypeOptions,
  getChargeTimingOptions,
  PaymentStatusOptions,
} from "app/routes/Calendar/EventForm/paymentHelpers";
import { SelectStyles } from "app/routes/Calendar/EventForm/constants";
import { groupFamilyPayers } from "app/utils/familyPayer";
import { flattenFamilyStudentProfiles } from "app/utils/familyStudentProfiles";
import { toast } from "react-toastify";
import CustomStripeProgramPrompt from "app/routes/Calendar/CustomStripeProgramPrompt";
import ToolTip from "app/routes/Scheduling/ToolTip";

const filteredPaymentStatusOptions = PaymentStatusOptions.filter(
  (o) => o.value !== "failed" && o.value !== "past_due"
);

const parseRegistrationErrors = (errors: any[]): Record<string, string[]> => {
  const result = errors.reduce((acc: Record<string, string[]>, err: any) => {
    const field = err.source?.parameter || '_general';
    const messages = (err.details || []).map((d: any) => d.message).filter(Boolean);
    if (messages.length > 0) {
      acc[field] = messages;
    }
    return acc;
  }, {});

  if (Object.keys(result).length === 0) {
    result['_general'] = ['An unexpected error occurred. Please try again.'];
  }
  return result;
};

interface RegisterAthleteModalProps {
  programModel: any;
  isModalOpen: boolean;
  modalHandler: () => void;
  stripeIntegrationEnabled: boolean;
  onRegistrationSuccess: (response: any) => Promise<void>;
}

const ensureRegistrationModel = (item: any): StudentProgramRegistrationModel => {
  if (item instanceof StudentProgramRegistrationModel) {
    return item;
  }

  const profile = item instanceof StudentProfileModel
    ? item
    : new StudentProfileModel({
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        phone: item.phone,
        profilePicture: item.profilePicture || item.avatar || '',
        calendarSubscriptionUrl: item.calendarSubscriptionUrl,
      });

  return new StudentProgramRegistrationModel(
    {
      id: item.registrationId || 0,
      status: item.status || 'pending',
      createdAt: item.createdAt || new Date().toISOString(),
      canRemove: item.canRemove !== undefined ? item.canRemove : true,
      paymentMethod: item.paymentMethod || 'offline',
      expiresAt: item.expiresAt,
    },
    profile
  );
};

const PaymentStatusSingleValue = ({ data, ...rest }: any) => (
  <components.SingleValue {...rest} data={data}>
    <div style={{ display: "flex", alignItems: "center" }}>
      <span
        className={styles.payment_status_dot}
        style={{ backgroundColor: data.color }}
      />
      {data.label}
    </div>
  </components.SingleValue>
);

const PaymentStatusOption = ({ data, innerRef, innerProps, isSelected }: any) => (
  <div
    ref={innerRef}
    {...innerProps}
    style={{
      padding: "10px 12px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      backgroundColor: isSelected ? "#f0f0f0" : "white",
    }}
  >
    <span
      className={styles.payment_status_dot}
      style={{ backgroundColor: data.color }}
    />
    {data.label}
  </div>
);

const RegisterAthleteModal = ({
  programModel,
  isModalOpen,
  modalHandler,
  stripeIntegrationEnabled,
  onRegistrationSuccess,
}: RegisterAthleteModalProps) => {
  const { t } = useTranslation();

  const methods = useForm<{ studentProfiles: any[] }>({
    defaultValues: { studentProfiles: [] },
  });
  const watchStudents = methods.watch("studentProfiles");
  const { setError, clearErrors, formState: { errors: formErrors } } = methods;

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"online" | "offline">("online");
  const [onlinePaymentType, setOnlinePaymentType] = useState<"send_invoice" | "card_on_file">("send_invoice");
  const [chargeTiming, setChargeTiming] = useState<"immediate" | "after_session">("immediate");
  const [paymentStatus, setPaymentStatus] = useState<string>("unpaid");
  const [showStripePrompt, setShowStripePrompt] = useState(false);

  const onlinePaymentTypeDropdownOptions = OnlinePaymentTypeOptions.filter(
    (opt) => opt.value !== "checkout"
  );
  const chargeTimingOptions = getChargeTimingOptions(onlinePaymentType).map((opt) =>
    opt.value === "immediate"
      ? { ...opt, label: onlinePaymentType === "send_invoice" ? "Invoice when Registered" : "Charge when Registered" }
      : opt
  );

  // CN-1323 (CFTB-3321): group the candidates by family payer so each family
  // gets its own "billed to parent / guardian" note nested under its members —
  // even when the list also holds other families or unrelated athletes. The
  // family fields (headExternalId / headFullName) live on the raw picker
  // selections, not on the registration models (ensureRegistrationModel drops
  // them), so group the raw selections and map each member to its model.
  const { registrationGroups, waitlistGroups } = useMemo(() => {
    const spotsAvailable = programModel.spotsRemaining || 0;
    const registered = watchStudents.slice(0, spotsAvailable);
    const waitlisted = programModel.withWaitlist
      ? watchStudents.slice(spotsAvailable)
      : [];

    const toGroups = (athletes: any[]) =>
      groupFamilyPayers(athletes).map((group) => ({
        key: group.key,
        isFamily: group.isFamily,
        payerName: group.payerName,
        registrations: group.members.map(ensureRegistrationModel),
      }));

    return {
      registrationGroups: toGroups(registered),
      waitlistGroups: toGroups(waitlisted),
    };
  }, [watchStudents, programModel.spotsRemaining, programModel.withWaitlist]);

  const registrationCount = registrationGroups.reduce(
    (total, group) => total + group.registrations.length,
    0,
  );
  const waitlistCount = waitlistGroups.reduce(
    (total, group) => total + group.registrations.length,
    0,
  );

  // Render each family group's members followed by its own paying-parent note;
  // standalone athletes (isFamily === false) render with no note.
  const renderCandidateGroups = (groups: any[]) =>
    groups.map((group) => (
      <React.Fragment key={group.key}>
        <UserItems
          userList={group.registrations}
          onClose={handleRemoveClick}
          showEmail={false}
          showPaymentMethod={false}
          separatorStyle={styles.line_separator}
          allowRemove
        />
        {!programModel.isFree && group.isFamily && (
          <div className={styles.paying_parent_note}>
            <span className={styles.paying_parent_label}>
              {t("Paying User")}:
            </span>{" "}
            <strong>{group.payerName || t("Parent / Guardian")}</strong>
            <div className={styles.paying_parent_hint}>
              {t("Family member registrations are billed to the parent / guardian.")}
            </div>
          </div>
        )}
      </React.Fragment>
    ));

  // CN-2868: the program decides what it accepts, so its collect type is read
  // first. Stripe availability used to be checked ahead of it and sent offline
  // on an online-only program, which the backend then rejected with "This
  // program only accepts online payments".
  useEffect(() => {
    if (programModel.collectType === "offline") {
      setPaymentMethod("offline");
      return;
    }

    if (programModel.collectType === "online") {
      setPaymentMethod("online");
      return;
    }

    // 'both': Stripe availability and the program's own default pick the side.
    setPaymentMethod(
      stripeIntegrationEnabled && programModel.onlineDefault ? "online" : "offline",
    );
  }, [programModel.collectType, programModel.onlineDefault, stripeIntegrationEnabled]);

  const loadStudents = async (
    inputValue: string,
    _currentOptions: any[],
    additional: { page: number },
    newStudents: any[] = [],
  ) => {
    const selectedIds = new Set(watchStudents.map((s) => s.id));
    const newStudentIds = new Set(newStudents.map((s) => s.id));
    const page = additional?.page || 1;

    // CN-1323 (CFTB-3281): nest managed children under their head
    // (options[include][]=children) when browsing AND searching — the BE expands
    // a matched child back to its parent + siblings, so families never split.
    const query = inputValue
      ? { query: inputValue, per_page: 20, page, options: { include: ["children"] } }
      : { per_page: 20, page, options: { include: ["children"] } };

    try {
      const response = await schedulingFetchGet(
        `/programs/${programModel.id}/registerable_student_profiles`,
        query,
        { withHeaders: true }
      );

      const { students, headers } = response;
      const currentPage = Number(headers.get("Current-Page"));
      const totalPages = Number(headers.get("Total-Pages"));

      const filteredStudents = flattenFamilyStudentProfiles(students).filter(
        (student: any) => !selectedIds.has(student.id) && !newStudentIds.has(student.id)
      );

      return {
        options: filteredStudents,
        hasMore: currentPage < totalPages,
        additional: { page: currentPage < totalPages ? currentPage + 1 : null },
      };
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(t("Failed to search athletes. Please try again."));
      return { options: [], hasMore: false, additional: { page: null } };
    }
  };

  const handleRemoveClick = (registration: StudentProgramRegistrationModel) => {
    const studentId = registration.studentProfile?.id || registration.id;
    const updated = watchStudents.filter((student) => {
      const id = student.studentProfile?.id || student.id;
      return id !== studentId;
    });
    methods.setValue("studentProfiles", updated);
  };

  const handleSubmitRegistration = async () => {
    setShowStripePrompt(false);
    setLoading(true);

    const studentPayloads = watchStudents.map((student) => {
      // Free clinics collect no payment, so no payment metadata is sent.
      if (programModel.isFree) {
        return { id: student.id };
      }

      const payload: any = {
        id: student.id,
        payment_method: paymentMethod,
      };

      if (paymentMethod === "online") {
        payload.online_payment_type = onlinePaymentType;
        payload.charge_timing = chargeTiming;
      } else {
        payload.payment_status = paymentStatus === "unpaid" ? "pending" : paymentStatus;
      }

      return payload;
    });

    try {
      const response = await schedulingFetchPost(`/programs/${programModel.id}/register`, {
        student_profile_ids: studentPayloads,
      });
      methods.reset();
      modalHandler();
      toast.success(t("Athletes registered successfully"));
      await onRegistrationSuccess(response);
    } catch (error) {
      console.error("Registration error:", error);
      if (error && error.errors && Array.isArray(error.errors)) {
        clearErrors();
        const parsed = parseRegistrationErrors(error.errors);
        Object.entries(parsed).forEach(([field, messages]) => {
          setError(field as any, { type: 'custom', message: messages.join(', ') });
        });
      } else {
        toast.error(t("An unexpected error has occurred."));
      }
    } finally {
      setLoading(false);
    }
  };

  const onFormSubmit = () => {
    clearErrors();
    if (
      !programModel.isFree &&
      paymentMethod === "online" &&
      onlinePaymentType === "send_invoice" &&
      programModel.spotsRemaining > 0
    ) {
      setShowStripePrompt(true);
    } else {
      handleSubmitRegistration();
    }
  };

  const programTitle = programModel.title || "";
  const displayTitle =
    programTitle.length > 25
      ? `${programTitle.substring(0, 25)}...`
      : programTitle;

  return (
    <>
      <ModalLayout
        isOpen={isModalOpen}
        onClose={modalHandler}
        classNames={`${styles.modal} event_form_container`}
      >
        <div>
          <div className={styles.modal_header}>
            <div className={styles.titleRow}>
              <h2
                className={styles.truncatedText}
                title={programTitle.length > 25 ? programTitle : ""}
              >
                {t("Register to")} &quot;{displayTitle}&quot;
              </h2>
              {programTitle.length > 25 && (
                <div className={styles.toolTipAfterEllipsis}>
                  <ToolTip text={programTitle} orientation="right" />
                </div>
              )}
            </div>
            <img
              src={closeIcon}
              alt="close"
              className={styles.close_icon}
              onClick={modalHandler}
            />
          </div>

          <div className={styles.add_view_form}>
            <p className={styles.helper_text}>
              {programModel.isFree
                ? t("Select the athletes you want to register.")
                : t("Choose a payment method, then select the athletes you want to register.")}
            </p>
            <div className={styles.fields_group}>
              {/* Payment Fields Row */}
              {!programModel.isFree && (
              <div className={styles.icon_row}>
                <img src={DollarIcon} alt="" className={styles.row_icon_top} />
                <div className={styles.row_content}>
                  <div className={styles.payment_row}>
                    <PaymentMethodDropdown
                      collectType={programModel.collectType || "offline"}
                      value={paymentMethod}
                      onChange={setPaymentMethod}
                      stripeIntegrationEnabled={stripeIntegrationEnabled}
                    />
                    {paymentMethod === "online" && (
                      <>
                        <div className={styles.payment_sub_field}>
                          <label className={styles.sr_only}>
                            {t("Online Payment Type")}
                          </label>
                          <Select
                            options={onlinePaymentTypeDropdownOptions}
                            value={onlinePaymentTypeDropdownOptions.find(
                              (opt) => opt.value === onlinePaymentType
                            )}
                            onChange={(selected: any) =>
                              setOnlinePaymentType(selected?.value)
                            }
                            styles={SelectStyles}
                            menuPortalTarget={document.body}
                            isSearchable={false}
                          />
                        </div>
                        <div className={styles.payment_sub_field}>
                          <label className={styles.sr_only}>
                            {t("Charge Timing")}
                          </label>
                          <Select
                            options={chargeTimingOptions}
                            value={
                              chargeTimingOptions.find(
                                (opt) => opt.value === chargeTiming
                              ) || chargeTimingOptions[0]
                            }
                            onChange={(selected: any) =>
                              setChargeTiming(selected?.value)
                            }
                            styles={SelectStyles}
                            menuPortalTarget={document.body}
                            isSearchable={false}
                          />
                        </div>
                      </>
                    )}
                    {paymentMethod === "offline" && (
                      <div className={styles.payment_sub_field}>
                        <label className={styles.sr_only}>
                          {t("Payment Status")}
                        </label>
                        <Select
                          options={filteredPaymentStatusOptions}
                          value={
                            filteredPaymentStatusOptions.find(
                              (opt) => opt.value === paymentStatus
                            ) || filteredPaymentStatusOptions[0]
                          }
                          onChange={(selected: any) =>
                            setPaymentStatus(selected?.value)
                          }
                          styles={SelectStyles}
                          menuPortalTarget={document.body}
                          isSearchable={false}
                          components={{
                            SingleValue: PaymentStatusSingleValue,
                            Option: PaymentStatusOption,
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {(formErrors as any).payment_method && (
                    <span className={styles.field_error}>
                      {(formErrors as any).payment_method.message}
                    </span>
                  )}
                </div>
              </div>
              )}

              {/* Athlete Selector Row */}
              <div className={styles.icon_row}>
                <img src={AccountCircleIcon} alt="" className={styles.row_icon} />
                <div className={styles.row_content} data-testid="dropdown-button">
                  <FormProvider {...methods}>
                    <SchedulingUserSelector
                      name="studentProfiles"
                      control={methods.control}
                      placeholder={t("Search Athletes")}
                      loadOptions={loadStudents}
                      multiSelect
                      canAddNewAthletes
                      showSelection={false}
                      hideCreateSpace
                    />
                  </FormProvider>
                </div>
              </div>
            </div>

            {/* Candidates Sections */}
            <div className={styles.column_container}>
              {registrationCount > 0 && (
                <div>
                  <div className={styles.section_header}>
                    {t("Registration Candidates")}
                  </div>
                  {renderCandidateGroups(registrationGroups)}
                </div>
              )}

              {waitlistCount > 0 && (
                <div>
                  <div className={styles.section_header}>
                    {t("Waitlist Candidates")}
                  </div>
                  {renderCandidateGroups(waitlistGroups)}
                </div>
              )}
            </div>
          </div>

          {(formErrors as any)._general && (
            <div className={styles.form_error}>
              {(formErrors as any)._general.message}
            </div>
          )}

          <div className={styles.button_wrapper}>
            <Button
              buttonType="light-primary"
              title={t("Cancel")}
              onClickFunc={modalHandler}
              data-testid="cancel-edit"
            />
            <Button
              buttonType="primary"
              title={loading ? t("Registering...") : t("Register")}
              onClickFunc={onFormSubmit}
              disabled={loading || watchStudents.length === 0}
            />
          </div>
        </div>
      </ModalLayout>

      {showStripePrompt && (
        <CustomStripeProgramPrompt
          data={{
            isOpen: showStripePrompt,
            title: t("Send the invoice to the athlete via Stripe?"),
            content: t(
              "All registered athletes will receive invoices through Stripe."
            ),
            confirmText: t("Send Invoices"),
            cancelText: t("Cancel"),
            confirmDanger: false,
            isRecurring: false,
            titleIcon: true,
            isLoading: loading,
          }}
          actions={{
            onCancel: () => setShowStripePrompt(false),
            onConfirm: handleSubmitRegistration,
          }}
        />
      )}
    </>
  );
};

export default memo(RegisterAthleteModal);
